import { deepCopyObject, isNullOrEmpty, isObjectNull } from "../../../helpers/ObjectHelpers";
import { CardListItemProps } from "../props/CardListItemProps";
import { ICardListDispatcherService } from "./ICardListDispatcherService";

class CardListDispatcherService implements ICardListDispatcherService {

    _source: CardListItemProps[] = [];
    _onReset: (() => void) | null = null;
    _showTools: boolean = false;
    _setIsSelectActive: (isSelectActive: boolean) => void = () => { };
    _maxSelectable: number = 0;
    _refs: any = null;
    _setLocalSource: (source: CardListItemProps[]) => void = () => { };

    constructor(
        onReset: (() => void) | null = null,
        showTools: boolean = false,
        setIsSelectActive: (isSelectActive: boolean) => void = () => { },
        setLocalSource: (source: CardListItemProps[]) => void = () => { }
    ) {
        this._onReset = onReset;
        this._showTools = showTools;
        this._setIsSelectActive = setIsSelectActive;
        this._setLocalSource = setLocalSource;
    }

    set source(props: CardListItemProps[]) {
        this._source = props;
        this._maxSelectable = props.filter(x => x.canSelect === true).length;
    }

    set refs(refs: any) {
        this._refs = refs;
    }

    onSelect(id: number, selected: boolean) {
        const item = this._source.find(x => x.id === id);
        
        if (!item || !item.canSelect || !item.onSelect) return;

        if (item.selected === selected) return;
        
        item.selected = selected;

        if (!isNullOrEmpty(item.systemName)) {
            item.onSelect(id, selected, item.systemName);
        } else {
            item.onSelect(id, selected);
        }

        this.onCheckState();
    }

    onToggleSelect() {
        const activeSelects = this.activeSelects();
        let show = false;

        if (activeSelects === 0) {
            show = true;
        } else {
            show = activeSelects !== this._maxSelectable;
        }
        this.onToggleItems(show);
    }

    onToggleItems(show: boolean) {
        for (const ref of this._refs.current) {
            if (isNullOrEmpty(ref)) continue;
            ref.onToggleSelect(show);
        }
    }

    onToggleAll(show: boolean) {
        this.onToggleItems(show);
        if (show) return;
        this.onSelectAll(false);
    }

    onSelectAll(select:boolean) {
        for (const ref of this._refs.current) {
            if (isNullOrEmpty(ref)) continue;
            ref.onSelect(select);
        }
    }

    onActivateSelect(id: number) {
        const item = this._source.find(x => x.id === id);
        if (!item) return;

        item.selectActive = true;

        this.onCheckState();
    }

    onDeactivateSelect(id: number) {
        const item = this._source.find(x => x.id === id);
        if (!item) return;

        item.selectActive = false;

        this.onCheckState();
    }

    onDeleted(deletedIds: number[]) {
        for (const ref of this._refs.current) {
            if (isNullOrEmpty(ref)) continue;
            for (const deletedId of deletedIds) {
                ref.onDeleted(deletedId);
                const item = this._source.find(x => x.id === deletedId);
                if (item) {
                    item.deleted = true;
                }
            }
        }
        
        const filteredSource = this._source.filter((x: CardListItemProps) => !deletedIds.includes(x.id ?? 0));
        setTimeout(() => {
            this._setLocalSource(filteredSource);
        }, 500);
    }

    onUpdated(updatedItem: CardListItemProps, sortDirection: string) {

        const filteredSource = this._source.filter((x: CardListItemProps) => x.id !== updatedItem.id);
        const sortValues: any[] = filteredSource.map(x => ({ id: x.id, sortValue: x.sortValue }));
        const newSortValue = { id: updatedItem.id, sortValue: updatedItem.sortValue };

        sortValues.push(newSortValue);

        const index = this.findIndex(sortDirection, sortValues, updatedItem);
        
        filteredSource.splice(index, 0, updatedItem);

        this._setLocalSource(filteredSource);
    }

    onCheckState() {
        const activeSelects = this.activeSelects();
        
        if (activeSelects === 0 && this._onReset) {
            this._onReset();
        }
        
        if (!this._showTools) return;
        
        this._setIsSelectActive(activeSelects === this._maxSelectable);
    }

    activeSelects() {
        const result = this._source.filter(x => x.canSelect).map(x => x.selectActive ?? false);
        return result.filter(x => x === true).length;
    }

    isListItemSelectActive(listItem: CardListItemProps) {
        if (!listItem) 
            return false;

        if (!listItem.canSelect) 
            return false;

        if ("selectActive" in listItem)
            return listItem.selectActive === true;

        return false;
    }

    isListItemDeleted(listItem: any) {
        if (!listItem) return false;

        if ("deleted" in listItem)
            return listItem.deleted === true;

        return false;
    }

    findIndex(sortDirection: string, sortValues: any[], item: CardListItemProps): number {
        sortValues.sort((a, b) => {
            let aSortValue = a.sortValue;
            let bSortValue = b.sortValue;

            if (!isNullOrEmpty(aSortValue) && !isNullOrEmpty(bSortValue)) {
                let aDateValue = new Date(aSortValue);
                let bDateValue = new Date(bSortValue);

                if (typeof aSortValue === "number" && typeof bSortValue === "number") {
                    aSortValue = Number(aSortValue);
                    bSortValue = Number(bSortValue);
                }
                else if (aDateValue && bDateValue) {
                    aSortValue = aDateValue;
                    bSortValue = bDateValue;
                }
                else if (typeof aSortValue === "string" && typeof bSortValue === "string") {
                    aSortValue = String(aSortValue);
                    bSortValue = String(bSortValue);
                } else {
                    console.log("her 4");
                }

                if (sortDirection === "asc") {
                    return aSortValue - bSortValue; // Ascending order
                } else {
                    return bSortValue - aSortValue; // Descending order
                }

            } else if (!isNullOrEmpty(aSortValue) && isNullOrEmpty(bSortValue)) {
                // If aSortValue is not null and bSortValue is null, sort a before b
                return -1;
            } else if (isNullOrEmpty(aSortValue) && !isNullOrEmpty(bSortValue)) {
                // If aSortValue is null and bSortValue is not null, sort b before a
                return 1;
            } else {
                // Both are null, consider them equal
                return 0;
            }
        });

        return sortValues.findIndex((x) => x.id === item.id);
    }

    onReset(id: number) {
        for (const ref of this._refs.current) {
            if (isNullOrEmpty(ref)) continue;
            ref.onReset(id);
        }
    }
}

export default CardListDispatcherService;
